/**
 * AI:
 * Status: "in progress"
 * Type: Logic
 * SubType: WidgetConfig
 * Reason: Derives the result columns of a QL-Widget-Builder definition in definition order (with
 *   German labels, date-group precision and the auto-added onlyFirst FK flagged hidden) and turns
 *   them into the WidgetForm the table/list widget renders - the engine's view-pipe form is an
 *   unordered map that also carries filter-only and meta-link fields
 */
import {QlQueryDto} from "../../../shared/ql/data/dto/ql-query.dto";
import {QlJoinDto} from "../../../shared/ql/data/dto/ql-join.dto";
import {QlQueryContentFieldDto} from "../../../shared/ql/data/dto/ql-query-content-field.dto";
import {QlBuilderChildNode, QlBuilderJoinNode, QlBuilderModel} from "../../../shared/ql/builder/ql-builder-model";
import {QlBuilderQueryConverter} from "../../../shared/ql/builder/ql-builder-query-converter";
import {WidgetForm} from "../../../shared/widget/widget-form";
import {WidgetConfigFormLayout} from "../../../shared/widget/widget-config-form-layout";
import {WidgetConfigFormField} from "../../../shared/widget/widget-config-form-field";
import {UiQlWidgetColumn} from "../../interface/ui-ql-widget-column.interface";

/** Optional knowledge that makes the derived columns more precise; both are safe to omit. */
export interface UiQlWidgetColumnOptions {

    /**
     * The builder tree the definition was converted from, when the caller has it. Only used to tell
     * a back-reference FK the USER selected from the one `QlBuilderQueryConverter.toQlQuery` added
     * to an `onlyFirst` join. Without it the converter's placement (last field of the join) decides.
     */
    model?: QlBuilderModel | null;

    /** Labels per result key (`QlBuilderKeyLabels.labels`), resolved from the entity metadata. */
    labels?: ReadonlyMap<string, string> | null;
}

/**
 * Pure derivation of the columns a QL-Widget-Builder widget shows, plus the `WidgetForm` that
 * makes the table/list widget render exactly those columns in that order (fe_core_widget TW-2:
 * a curated column set goes through the widget layout, never through an own table).
 *
 * Column order is definition order: start fields, then the joins depth-first as they appear in
 * the definition. Result keys follow the engine: `<as>.<as|name>` for entity/groupBy fields,
 * `<as>.attribute` for a `+`/`_` meta attribute, `<hostAs>.ext_<typeFieldId>` for a side-car field.
 */
export class UiQlWidgetColumns {

    /** German label of an aggregate function (`count(*)` is just "Anzahl"). */
    static readonly FUNCTION_LABELS: Readonly<Record<string, string>> = {
        count: 'Anzahl',
        sum: 'Summe',
        avg: 'Durchschnitt',
        min: 'Minimum',
        max: 'Maximum',
    };

    /** German suffix of a date-group column. */
    static readonly PRECISION_LABELS: Readonly<Record<string, string>> = {
        day: 'je Tag',
        month: 'je Monat',
        year: 'je Jahr',
    };

    /**
     * The columns of a definition in definition order.
     *
     * @param query   the definition (not mutated)
     * @param options builder model and/or metadata labels, when available
     */
    static derive(query: QlQueryDto, options?: UiQlWidgetColumnOptions): UiQlWidgetColumn[] {

        const columns: UiQlWidgetColumn[] = [];
        const start = query?.start;
        if (!start?.as) {
            return columns;
        }

        const labels = options?.labels ?? null;
        const modelJoins = UiQlWidgetColumns.modelJoinsByAlias(options?.model);

        for (const field of start.fields ?? []) {
            columns.push(UiQlWidgetColumns.startColumn(start.as, field, labels));
        }

        const walk = (joins: QlJoinDto[] | undefined, hostAs: string): void => {
            for (const join of joins ?? []) {
                if (join.type === 'meta') {
                    // a +/_ attribute comes back as <as>.attribute (a + join is not renderable, S2)
                    const key = `${join.as}.attribute`;
                    columns.push({key, label: labels?.get(key) ?? null, hidden: false});
                    continue;
                }

                const isSideCar = !!join.joinParameters && Object.keys(join.joinParameters).length > 0;
                if (isSideCar) {
                    // side-car columns are keyed on the HOST alias (spike S6)
                    for (const field of join.fields ?? []) {
                        const key = `${hostAs}.ext_${(field.name ?? '').replace(/^ext_/, '')}`;
                        columns.push({key, label: labels?.get(key) ?? null, hidden: false});
                    }
                    continue;
                }

                const autoFk = join.onlyFirst ? QlBuilderQueryConverter.onlyFirstForeignKeyField(join.name) : null;
                const fields = join.fields ?? [];
                fields.forEach((field, index) => {
                    const key = `${join.as}.${field.as || field.name}`;
                    const hidden = !!autoFk
                        && field.name === autoFk
                        && !UiQlWidgetColumns.isExplicit(modelJoins, join.as, autoFk, index === fields.length - 1);
                    columns.push({key, label: labels?.get(key) ?? null, hidden});
                });

                walk(join.joins, join.as);
            }
        };
        walk(start.joins, start.as);

        return columns;
    }

    /** The column of a result key, or `undefined`. */
    static find(columns: UiQlWidgetColumn[] | undefined, key: string | undefined): UiQlWidgetColumn | undefined {
        return key ? (columns ?? []).find(column => column.key === key) : undefined;
    }

    /**
     * The `widgetForm` parameter of a table/list widget: every visible column as a layout field in
     * order, with the known German label as header. `null` when there is no visible column — the
     * widget then falls back to the engine's form.
     */
    static widgetForm(columns: UiQlWidgetColumn[]): WidgetForm | null {

        const visible = (columns ?? []).filter(column => !column.hidden);
        if (!visible.length) {
            return null;
        }

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;          // default-layout convention: uiHidden=false fields are shown
        layout.fieldsConfigured = true;

        visible.forEach((column, index) => {
            const field = WidgetConfigFormField.create(column.key, '', '');
            if (column.label) {
                field.uiLabel = column.label;
            }
            field.uiPriority = index;      // definition order
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }

    // ------------------------------------------------------------------
    // internals
    // ------------------------------------------------------------------

    private static startColumn(
        as: string,
        field: QlQueryContentFieldDto,
        labels: ReadonlyMap<string, string> | null): UiQlWidgetColumn {

        const key = `${as}.${field.as || field.name}`;
        const known = labels?.get(key) ?? null;

        if (field.fieldType === 'dateGroup' && field.dateGroupPrecision) {
            const suffix = UiQlWidgetColumns.PRECISION_LABELS[field.dateGroupPrecision];
            return {
                key,
                label: known ?? (suffix ? `${field.name} (${suffix})` : field.name ?? null),
                dateGroupPrecision: field.dateGroupPrecision,
                hidden: false,
            };
        }

        if (field.fieldType === 'function' || field.function) {
            const fn = UiQlWidgetColumns.FUNCTION_LABELS[field.function ?? ''] ?? field.function ?? '';
            const label = field.name === '*' || !field.name ? fn : `${fn} ${field.name}`;
            return {key, label: known ?? (label || null), hidden: false};
        }

        return {key, label: known, hidden: false};
    }

    /** onlyFirst join nodes of the model by alias — the fields the USER picked. `null` without a model. */
    private static modelJoinsByAlias(model: QlBuilderModel | null | undefined): Map<string, QlBuilderJoinNode> | null {

        if (!model?.root) {
            return null;
        }

        const joins = new Map<string, QlBuilderJoinNode>();
        const walk = (children: QlBuilderChildNode[] | undefined): void => {
            for (const child of children ?? []) {
                if (child.kind === 'join') {
                    joins.set(child.alias, child);
                    walk(child.children);
                }
            }
        };
        walk(model.root.children);
        return joins;
    }

    /**
     * Whether the back-reference FK of an onlyFirst join was selected by the user. With the model:
     * exact (the field is in the node's own field list). Without: the converter appends the FK as
     * the LAST field, so a FK anywhere else must have been selected explicitly.
     */
    private static isExplicit(
        modelJoins: Map<string, QlBuilderJoinNode> | null,
        joinAs: string,
        fk: string,
        isLast: boolean): boolean {

        if (modelJoins) {
            const node = modelJoins.get(joinAs);
            return node ? node.fields.some(field => field.name === fk) : !isLast;
        }
        return !isLast;
    }
}
